/**
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): permissions come from the shared /api/me snapshot read once in the layout; this page no longer calls /api/me
 * Billing Page - Subscription management with Stripe
 * v2.82 - 2026-09-13 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: hide feature grid + usage bars for payment-mode tiers (one-time-purchase card instead); show all seven caps on subscription tiers using the existing -1=Unlimited formatting; formatFeatureValue treats null as "Not included" so a missing key never renders as "0"
 * v2.81 - 2026-06-10 - STORY-10E-C: fully catalog-driven — no tier-name/price literals; planIcons/planColors by sort_order position
 * v2.80 - 2026-06-05 - STORY8-STEP3: upgrade dialog reads price/limits from /api/plans catalog
 * v2.79 - 2026-03-13 - Trials purged: removed trial countdown card and trial-status fetch
 * v2.77 - 2026-02-05 - Fixed fetch('/api/me') to include X-Authorization header for Clerk JWT
 * v2.38 - Fixed credentials: 'include' for SWA auth - 2026-02-01
 * v2.33 - Added role-based permission checks - 2026-01-31
 * v2.28 - Full implementation with Stripe integration - 2026-01-30
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ToggleButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ToggleButtonGroup,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CreditCard as CardIcon,
  Receipt as InvoiceIcon,
  OpenInNew as ExternalIcon,
  Download as DownloadIcon,
  Bolt as ProIcon,
  Business as EnterpriseIcon,
  Star as FreeIcon,
  TrendingUp as UsageIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import TopBar from '../components/TopBar';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { billingService, BillingResponse, Plan, plansService, Tier } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
// v2.81 - STORY-10E-C: catalog helpers replace hardcoded tier lookups
import { subscriptionTiers, findUpgradeTier, resolvePlanToTier, isFreeTier, hasUpgradePath, tierSortPosition } from '../utils/planCatalog';

// Format currency
const formatCurrency = (amount: number, currency: string = 'usd'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format feature value
// v2.82 - accept null/undefined as "Not included" so a missing key from the
// normalized billing shape never renders as a number (SESSION-101, 2026-09-13).
const formatFeatureValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'Not included';
  if (value === -1) return 'Unlimited';
  return value.toLocaleString();
};

// v2.81 - STORY-10E-C: icons + colors indexed by sort_order position (0=lowest, 1=mid, 2+=highest)
// No tier-name keys — position in the sorted subscription list drives the icon/color.
const POSITION_ICONS: React.ReactNode[] = [
  <FreeIcon sx={{ fontSize: 40, color: '#999' }} />,
  <ProIcon sx={{ fontSize: 40, color: '#F4A261' }} />,
  <EnterpriseIcon sx={{ fontSize: 40, color: '#008080' }} />,
];
const POSITION_COLORS = ['#999', '#F4A261', '#008080'];

function getPlanIcon(position: number): React.ReactNode {
  return POSITION_ICONS[Math.min(position, POSITION_ICONS.length - 1)];
}
function getPlanColor(position: number): string {
  return POSITION_COLORS[Math.min(position, POSITION_COLORS.length - 1)];
}

const Billing: React.FC = () => {
  // v4.28 - G10-B: permissions of the chosen company (server answer, read once in the layout)
  const { hasPermission } = usePermissions();
  const canViewBilling = hasPermission('viewBilling');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canManageBilling = hasPermission('manageBilling');

  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Upgrade dialog state
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [upgrading, setUpgrading] = useState(false);

  // Portal loading state
  const [openingPortal, setOpeningPortal] = useState(false);

  // v2.81 - STORY-10E-C: full catalog; helpers derive upgrade tier + current tier by alias
  const [catalogTiers, setCatalogTiers] = useState<Tier[]>([]);

  useEffect(() => {
    plansService.getPlans().then((tiers) => {
      setCatalogTiers(tiers);
    }).catch(() => {
      // catalogTiers stays [], upgrade dialog shows no price (no hardcoded fallback)
    });
  }, []);


  // Load billing data
  const loadBilling = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await billingService.getStatus();
      setBilling(response);
    } catch (err: any) {
      setError(err.userMessage || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBilling();

    // Check for success/canceled from Stripe redirect
    if (searchParams.get('success') === 'true') {
      setSuccess('Payment successful! Your subscription is now active.');
      window.history.replaceState({}, '', '/dashboard/billing');
    } else if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. No charges were made.');
      window.history.replaceState({}, '', '/dashboard/billing');
    }
  }, [loadBilling, searchParams]);

  // v2.81 - STORY-10E-C: upgrade to highest subscription tier from catalog (no literal tier_id)
  const handleUpgrade = async () => {
    const upgradeTier = findUpgradeTier(catalogTiers);
    if (!upgradeTier) return;
    try {
      setUpgrading(true);
      const response = await billingService.createCheckout({
        plan: upgradeTier.tier_id,
        billing_cycle: 'monthly'
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        setError(response.error || 'Failed to create checkout session');
        setUpgradeDialogOpen(false);
      }
    } catch (err: any) {
      setError(err.userMessage || 'Failed to start upgrade');
      setUpgradeDialogOpen(false);
    } finally {
      setUpgrading(false);
    }
  };

  // Open Stripe portal
  const handleOpenPortal = async () => {
    try {
      setOpeningPortal(true);
      const response = await billingService.createPortal();

      if (response.portal_url) {
        window.location.href = response.portal_url;
      } else {
        setError(response.error || 'Failed to open billing portal');
      }
    } catch (err: any) {
      setError(err.userMessage || 'Failed to open billing portal');
    } finally {
      setOpeningPortal(false);
    }
  };

  // Calculate usage percentage
  const getUsagePercent = (used: number, limit: number): number => {
    if (limit === -1) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  // v2.33 - Access denied for non-owners
  if (!canViewBilling) {
    return (
      <Box sx={{ p: 3 }}>
        <TopBar title="Billing" subtitle="Access Restricted" />
        <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
          <BlockIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#003366', mb: 2 }}>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            Only the account owner can view billing information.
          </Typography>
        </Container>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <TopBar title="Billing" subtitle="Manage your subscription" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const currentPlan = billing?.plan;
  const subscription = billing?.subscription;
  const usage = billing?.usage;
  const invoices = billing?.invoices || [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const availablePlans = billing?.available_plans || {};

  // v2.81 - STORY-10E-C: catalog-driven plan resolution (no tier-name literals)
  const subTiers = subscriptionTiers(catalogTiers);
  const currentCatalogTier = resolvePlanToTier(currentPlan?.id, subTiers);
  const upgradeTier = findUpgradeTier(catalogTiers);
  const currentPosition = tierSortPosition(currentCatalogTier, catalogTiers);
  const showUpgrade = hasUpgradePath(currentPlan?.id, catalogTiers);
  const currentIsFreePlan = isFreeTier(currentCatalogTier) || (currentCatalogTier === null && (currentPlan?.price ?? 0) === 0);
  // v2.82 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: gate the feature grid + usage bars.
  // Prefer the backend-emitted plan.payment_mode; fall back to the unfiltered catalog
  // lookup (subTiers filters out payment-mode rows, so it cannot answer this alone).
  const currentFullCatalogTier = resolvePlanToTier(currentPlan?.id, catalogTiers);
  const isPaymentModeTier =
    (currentPlan?.payment_mode === 'payment') ||
    (currentFullCatalogTier?.payment_mode === 'payment');

  // v2.34 - Mobile responsive padding - 2026-03-20
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar title="Billing" subtitle="Manage your subscription" />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Current Plan Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#BBCDE2', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  {getPlanIcon(currentPosition)}
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h5" sx={{ color: '#003366', fontWeight: 700 }}>
                      {currentPlan?.name || 'Free'} Plan
                    </Typography>
                    <Typography variant="h4" sx={{ color: getPlanColor(currentPosition), fontWeight: 700 }}>
                      {formatCurrency(currentPlan?.price || 0)}/month
                    </Typography>
                  </Box>
                </Box>

                {subscription && subscription.status !== 'none' && (
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label={subscription.status === 'active' ? 'Active' : subscription.status}
                      color={subscription.status === 'active' ? 'success' : 'warning'}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {subscription.cancel_at_period_end && (
                      <Chip label="Cancels at period end" color="warning" size="small" />
                    )}
                    {subscription.current_period_end && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {subscription.cancel_at_period_end ? 'Expires' : 'Renews'} on{' '}
                        {new Date(subscription.current_period_end).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* v2.82 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: payment-mode tiers
                    (e.g. insight_unlock) get a one-time-purchase card instead of the
                    subscription-shaped feature grid + usage bars - 2026-09-13 */}
                {isPaymentModeTier ? (
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#003366', mb: 2 }}>
                      One-time purchase
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      This plan is a one-time purchase. It does not include a
                      subscription's ongoing usage caps.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ color: '#003366', mb: 2 }}>
                      Plan Features
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.connections ?? null)} data connections`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.team_members ?? null)} team members`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.api_calls_monthly ?? null)} API calls/month`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.dashboards ?? null)} dashboards`} />
                      </ListItem>
                      {/* v2.82 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: three previously
                          hidden caps now visible on subscription tiers - 2026-09-13 */}
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.nlp_questions_monthly ?? null)} AI questions/month`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.nlp_qpm ?? null)} AI questions/minute`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${formatFeatureValue(currentPlan?.features.doc_pages_monthly ?? null)} document pages/month`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon sx={{ color: '#008080' }} /></ListItemIcon>
                        <ListItemText primary={`${currentPlan?.features.support || 'Community'} support`} />
                      </ListItem>
                    </List>
                  </>
                )}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  {/* v2.81 - STORY-10E-C: show iff a higher-sort_order subscription tier exists */}
                  {showUpgrade && upgradeTier && (
                    <Button
                      variant="contained"
                      onClick={() => setUpgradeDialogOpen(true)}
                      sx={{ bgcolor: '#F4A261', '&:hover': { bgcolor: '#e89251' } }}
                    >
                      {`Upgrade: ${formatCurrency(upgradeTier.price_monthly, upgradeTier.currency)}/mo`}
                    </Button>
                  )}
                  {subscription && subscription.status === 'active' && (
                    <Button
                      variant="outlined"
                      onClick={handleOpenPortal}
                      disabled={openingPortal}
                      startIcon={openingPortal ? <CircularProgress size={16} /> : <CardIcon />}
                      sx={{ borderColor: '#008080', color: '#008080' }}
                    >
                      Manage Subscription
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Card */}
          {/* v2.82 - SESSION-101 PLAN-LIMITS-DISPLAY-FIX: payment-mode tiers do not
              have subscription-shaped usage caps; hide the whole usage grid item for
              them so we do not render "0 / 0" bars - 2026-09-13 */}
          {!isPaymentModeTier && (
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#BBCDE2', height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <UsageIcon sx={{ fontSize: 40, color: '#003366' }} />
                  <Typography variant="h5" sx={{ color: '#003366', fontWeight: 700, ml: 2 }}>
                    Current Usage
                  </Typography>
                </Box>

                {/* Connections */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Data Connections</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {usage?.connections || 0} / {formatFeatureValue(currentPlan?.features.connections || 0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getUsagePercent(usage?.connections || 0, currentPlan?.features.connections || 0)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'white',
                      '& .MuiLinearProgress-bar': { bgcolor: '#008080' }
                    }}
                  />
                </Box>

                {/* Team Members */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Team Members</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {usage?.team_members || 0} / {formatFeatureValue(currentPlan?.features.team_members || 0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getUsagePercent(usage?.team_members || 0, currentPlan?.features.team_members || 0)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'white',
                      '& .MuiLinearProgress-bar': { bgcolor: '#008080' }
                    }}
                  />
                </Box>

                {/* API Calls */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">API Calls This Month</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {(usage?.api_calls_this_month || 0).toLocaleString()} / {formatFeatureValue(currentPlan?.features.api_calls_monthly || 0)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getUsagePercent(usage?.api_calls_this_month || 0, currentPlan?.features.api_calls_monthly || 0)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'white',
                      '& .MuiLinearProgress-bar': { bgcolor: '#008080' }
                    }}
                  />
                </Box>

                {currentIsFreePlan && showUpgrade && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Upgrade for more connections, team members, and API calls!
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          )}

          {/* Invoices */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#BBCDE2' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <InvoiceIcon sx={{ fontSize: 30, color: '#003366', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#003366', fontWeight: 700 }}>
                    Billing History
                  </Typography>
                </Box>

                {invoices.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No invoices yet
                  </Typography>
                ) : (
                  <List>
                    {invoices.map((invoice, index) => (
                      <React.Fragment key={invoice.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          secondaryAction={
                            <Box>
                              {invoice.invoice_pdf && (
                                <Tooltip title="Download PDF">
                                  <IconButton
                                    component="a"
                                    href={invoice.invoice_pdf}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ color: '#008080' }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {invoice.hosted_invoice_url && (
                                <Tooltip title="View Invoice">
                                  <IconButton
                                    component="a"
                                    href={invoice.hosted_invoice_url}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ color: '#008080' }}
                                  >
                                    <ExternalIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography fontWeight={600}>
                                  {formatCurrency(invoice.amount, invoice.currency)}
                                </Typography>
                                <Chip
                                  label={invoice.status}
                                  size="small"
                                  color={invoice.status === 'paid' ? 'success' : 'default'}
                                />
                              </Box>
                            }
                            secondary={formatDate(invoice.date)}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* v2.81 - STORY-10E-C: Upgrade Dialog — catalog-driven tier */}
      <Dialog
        open={upgradeDialogOpen}
        onClose={() => !upgrading && setUpgradeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#003366', color: 'white' }}>
          Upgrade to MyLojic
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Card sx={{ border: '2px solid #008080', bgcolor: '#f0f8f8', mt: 1 }}>
            <CardContent>
              {upgradeTier && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getPlanIcon(subTiers.length > 0 ? subTiers.length - 1 : 0)}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#003366' }}>
                    {upgradeTier.display_name}: Full Access
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#008080', fontWeight: 700 }}>
                    {`${formatCurrency(upgradeTier.price_monthly, upgradeTier.currency)}/month`}
                  </Typography>
                </Box>
              </Box>
              )}
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary={upgradeTier?.limits.connections === -1 ? 'Unlimited data connections' : `${formatFeatureValue(upgradeTier?.limits.connections ?? -1)} data connections`} />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary={upgradeTier?.limits.team_members === -1 ? 'Unlimited team members' : `${formatFeatureValue(upgradeTier?.limits.team_members ?? -1)} team members`} />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Automated AI pipeline (discovery → insights)" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="KPI dashboard + auto-generated reports" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Priority support" />
                </ListItem>
              </List>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                You'll be redirected to Stripe to complete payment. Cancel anytime.
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUpgradeDialogOpen(false)} disabled={upgrading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpgrade}
            disabled={upgrading || !upgradeTier}
            startIcon={upgrading ? <CircularProgress size={16} /> : null}
            sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
          >
            {upgrading ? 'Redirecting to Stripe...' : upgradeTier ? `Subscribe: ${formatCurrency(upgradeTier.price_monthly, upgradeTier.currency)}/month` : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
